import { m } from "@mojo/i18n";
import { createFileRoute } from "@tanstack/react-router";
import {
	Bug,
	CheckCircle,
	Clock,
	Lightbulb,
	ListTodo,
	Target,
	TrendingUp,
} from "lucide-react";
import { useId } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { PageHeader } from "@/components/layout";
import { Badge, Card } from "@/components/ui";
import {
	type CycleInfo,
	getCyclesOverview,
	getDashboardStats,
	getIssuesTimeSeries,
	type IssuesTrendPoint,
} from "@/data/dashboard";

export const Route = createFileRoute("/_authenticated/")({
	loader: async () => {
		const [stats, trends, cycles] = await Promise.all([
			getDashboardStats(),
			getIssuesTimeSeries(),
			getCyclesOverview(),
		]);
		return { stats, trends, cycles };
	},
	component: DashboardPage,
});

function DashboardPage() {
	const { stats, trends, cycles } = Route.useLoaderData();

	return (
		<div>
			<PageHeader
				title={m.dashboard_title()}
				description={m.dashboard_description()}
			/>

			{/* Stats Grid */}
			<div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
				<StatCard
					icon={<ListTodo className="h-5 w-5" />}
					label={m.total_issues()}
					value={stats.totalIssues}
					variant="default"
				/>
				<StatCard
					icon={<CheckCircle className="h-5 w-5" />}
					label={m.resolved()}
					value={stats.resolvedIssues}
					variant="success"
				/>
				<StatCard
					icon={<Target className="h-5 w-5" />}
					label={m.cycle_success()}
					value={
						stats.cycleSuccessRate !== null
							? `${stats.cycleSuccessRate}%`
							: "N/A"
					}
					variant="default"
				/>
				<StatCard
					icon={<TrendingUp className="h-5 w-5" />}
					label={m.resolution_rate()}
					value={`${stats.resolutionRate}%`}
					variant="accent"
				/>
			</div>

			{/* Quick Stats Row */}
			<div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
				<Card>
					<div className="flex items-center gap-4">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-bug/10">
							<Bug className="h-5 w-5 text-bug" />
						</div>
						<div>
							<p className="font-bold font-mono text-2xl text-text-primary">
								{stats.openBugs}
							</p>
							<p className="text-sm text-text-secondary">{m.open_bugs()}</p>
						</div>
					</div>
				</Card>
				<Card>
					<div className="flex items-center gap-4">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-status-progress/10">
							<Clock className="h-5 w-5 text-status-progress" />
						</div>
						<div>
							<p className="font-bold font-mono text-2xl text-text-primary">
								{stats.inProgress}
							</p>
							<p className="text-sm text-text-secondary">{m.in_progress()}</p>
						</div>
					</div>
				</Card>
				<Card>
					<div className="flex items-center gap-4">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-feature/10">
							<Lightbulb className="h-5 w-5 text-feature" />
						</div>
						<div>
							<p className="font-bold font-mono text-2xl text-text-primary">
								{stats.plannedFeatures}
							</p>
							<p className="text-sm text-text-secondary">
								{m.planned_features()}
							</p>
						</div>
					</div>
				</Card>
			</div>

			{/* Main Content */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				{/* Issues Trend Chart */}
				<Card className="lg:col-span-2">
					<h3 className="mb-4 font-semibold text-lg text-text-primary">
						{m.issues_trend()}
					</h3>
					{trends.length > 0 ? (
						<IssuesTrendChart data={trends} />
					) : (
						<p className="text-sm text-text-tertiary">
							{m.no_data_available()}
						</p>
					)}
				</Card>

				{/* Cycles */}
				<Card>
					<h3 className="mb-4 font-semibold text-lg text-text-primary">
						{m.cycles()}
					</h3>
					<div className="space-y-4">
						{cycles.current ? (
							<CyclePreview cycle={cycles.current} />
						) : (
							<p className="text-sm text-text-tertiary">
								{m.no_active_cycle()}
							</p>
						)}
						{cycles.previous.length > 0 && (
							<div className="space-y-3 border-border border-t pt-4">
								<p className="font-medium text-text-tertiary text-xs uppercase tracking-wide">
									{m.previous()}
								</p>
								{cycles.previous.map((cycle) => (
									<CyclePreview key={cycle.number} cycle={cycle} compact />
								))}
							</div>
						)}
					</div>
				</Card>
			</div>
		</div>
	);
}

interface StatCardProps {
	icon: React.ReactNode;
	label: string;
	value: string | number;
	trend?: number;
	variant?: "default" | "accent" | "success";
}

function StatCard({
	icon,
	label,
	value,
	trend,
	variant = "default",
}: StatCardProps) {
	const variantClasses = {
		default: "text-text-tertiary",
		accent: "text-accent",
		success: "text-status-done",
	};

	return (
		<Card className="animate-in">
			<div className="flex items-start justify-between">
				<div
					className={`rounded-lg bg-bg-secondary p-2 ${variantClasses[variant]}`}
				>
					{icon}
				</div>
				{trend !== undefined && (
					<span
						className={`font-mono text-xs ${trend >= 0 ? "text-status-done" : "text-bug"}`}
					>
						{trend >= 0 ? "+" : ""}
						{trend}%
					</span>
				)}
			</div>
			<div className="mt-4">
				<p className="font-bold font-mono text-2xl text-text-primary">
					{value}
				</p>
				<p className="text-sm text-text-secondary">{label}</p>
			</div>
		</Card>
	);
}

function IssuesTrendChart({ data }: { data: IssuesTrendPoint[] }) {
	const createdGradientId = `gradient-created-${useId()}`;
	const resolvedGradientId = `gradient-resolved-${useId()}`;

	return (
		<div className="h-[280px] w-full">
			<ResponsiveContainer
				width="100%"
				height="100%"
				minWidth={300}
				minHeight={280}
			>
				<AreaChart
					data={data}
					margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
				>
					<defs>
						<linearGradient id={createdGradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
							<stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
						</linearGradient>
						<linearGradient id={resolvedGradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
							<stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
						</linearGradient>
					</defs>
					<CartesianGrid
						strokeDasharray="3 3"
						stroke="rgba(255,255,255,0.05)"
						vertical={false}
					/>
					<XAxis
						dataKey="week"
						axisLine={false}
						tickLine={false}
						tick={{ fill: "#6b7280", fontSize: 11 }}
						dy={10}
					/>
					<YAxis
						axisLine={false}
						tickLine={false}
						tick={{ fill: "#6b7280", fontSize: 11 }}
						dx={-5}
					/>
					<Tooltip
						contentStyle={{
							backgroundColor: "#1f2937",
							border: "1px solid rgba(255,255,255,0.1)",
							borderRadius: "8px",
							boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
						}}
						labelStyle={{ color: "#f3f4f6", fontWeight: 600, marginBottom: 4 }}
						itemStyle={{ color: "#d1d5db", fontSize: 13 }}
					/>
					<Area
						type="monotone"
						dataKey="created"
						name={m.created()}
						stroke="#14b8a6"
						strokeWidth={2}
						fillOpacity={1}
						fill={`url(#${createdGradientId})`}
					/>
					<Area
						type="monotone"
						dataKey="resolved"
						name={m.resolved_label()}
						stroke="#22c55e"
						strokeWidth={2}
						fillOpacity={1}
						fill={`url(#${resolvedGradientId})`}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
}

function CyclePreview({
	cycle,
	compact = false,
}: {
	cycle: CycleInfo;
	compact?: boolean;
}) {
	const progress =
		cycle.totalIssues > 0
			? Math.round((cycle.completedIssues / cycle.totalIssues) * 100)
			: cycle.progress;

	const formatDate = (dateStr: string) => {
		return new Date(dateStr).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
		});
	};

	if (compact) {
		return (
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="font-mono text-text-tertiary text-xs">
						{m.cycle_number({ number: cycle.number.toString() })}
					</span>
					<span className="text-text-tertiary text-xs">·</span>
					<span className="text-text-secondary text-xs">
						{cycle.completedIssues}/{cycle.totalIssues}
					</span>
				</div>
				<span className="font-mono text-status-done text-xs">{progress}%</span>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div>
				<div className="mb-2 flex items-center justify-between">
					<Badge variant="accent">
						{m.cycle_number({ number: cycle.number.toString() })}
					</Badge>
					<span className="font-mono text-text-tertiary text-xs">
						{formatDate(cycle.startsAt)} - {formatDate(cycle.endsAt)}
					</span>
				</div>
				<h4 className="font-medium text-text-primary">
					{cycle.name || m.cycle_number({ number: cycle.number.toString() })}
				</h4>
				<p className="mt-1 text-sm text-text-secondary">
					{m.issues_completed({
						completed: cycle.completedIssues.toString(),
						total: cycle.totalIssues.toString(),
					})}
				</p>
			</div>
			<div className="space-y-2">
				<div className="flex justify-between text-sm">
					<span className="text-text-secondary">{m.progress()}</span>
					<span className="font-mono text-text-primary">{progress}%</span>
				</div>
				<div className="h-2 overflow-hidden rounded-full bg-bg-secondary">
					<div
						className="h-full rounded-full bg-accent transition-all"
						style={{ width: `${progress}%` }}
					/>
				</div>
			</div>
		</div>
	);
}
